import React from 'react'
import { Box } from '@chilipiper/design-system'
import { TimezoneSelect, LabelToggle, InputGroup } from '@chilipiper/design-system/src/new'
import { ScheduleTimezone } from '@chilipiper/api-client-user-availability'
import { getClientTimezone } from '@chilipiper/date-time'

type TimezoneSelectorProps = {
  value: Partial<ScheduleTimezone>
  onChange: (value: ScheduleTimezone) => void
  syncedTimezone: string | null | undefined
  labelToggleText?: string
  isCalendarConnected?: boolean
}

const disconnectedTooltipError = `Your Calendar integration seems to be disconnected. Connect again to be able to use all syncing functionalities.
Go to Integrations`

export const TimezoneSelector = ({
  value,
  onChange,
  syncedTimezone,
  labelToggleText,
  isCalendarConnected,
}: TimezoneSelectorProps) => {
  const toggleLabel = labelToggleText ?? 'Sync with my calendar'
  const isSyncFromCalendar = value.type === 'SyncFromCalendar'
  const hasSyncError = syncedTimezone === null && isSyncFromCalendar
  const tooltipError = isCalendarConnected
    ? `Your calendar's time zone is not synced. Switch off "${toggleLabel}" to set one manually.`
    : disconnectedTooltipError

  return (
    <Box>
      <InputGroup label={<InputGroup.Label position='top'>Timezone</InputGroup.Label>}>
        <InputGroup.Row>
          <LabelToggle
            variant='nested'
            onChange={value =>
              onChange(
                value
                  ? {
                      type: 'SyncFromCalendar',
                    }
                  : {
                      type: 'FixedTimezone',
                      timezone: syncedTimezone ?? getClientTimezone(),
                    }
              )
            }
            isChecked={isSyncFromCalendar}
            label={
              <LabelToggle.Label
                endContent={
                  hasSyncError ? (
                    <LabelToggle.EndContent
                      variant='iconTooltip'
                      iconTooltipVariant='error'
                      content={tooltipError}
                    />
                  ) : undefined
                }
              >
                {toggleLabel}
              </LabelToggle.Label>
            }
          />
        </InputGroup.Row>
        <InputGroup.Row>
          {hasSyncError ? null : (
            <TimezoneSelect
              data-id='availability-timezone'
              variant='nested'
              label={<TimezoneSelect.Label position='hidden'>Timezone</TimezoneSelect.Label>}
              isDisabled={isSyncFromCalendar}
              value={
                value.type === 'FixedTimezone' ? value.timezone : (syncedTimezone ?? undefined)
              }
              onChange={value =>
                onChange({
                  type: 'FixedTimezone',
                  timezone: value,
                })
              }
            />
          )}
        </InputGroup.Row>
      </InputGroup>
    </Box>
  )
}
